import { useNavigate, useLocation } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { IconButton } from '../ui/IconButton'
import { ThemeSwitcher } from './ThemeSwitcher'
import { useCurrentPageTitle } from '../../hooks/usePageTitle'
import { useShopStore } from '../../stores/shop'
import { useAuthStore } from '../../stores/auth'
import { cn } from '../../lib/utils'

interface TopBarProps {
  actions?: React.ReactNode
  className?: string
}

export function TopBar({ actions, className }: TopBarProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const pageTitle = useCurrentPageTitle()
  const fallbackShopName = useShopStore((s) => s.shopName)
  const shops = useAuthStore((s) => s.shops)
  const currentShopId = useAuthStore((s) => s.currentShopId)
  const shopName = shops.find((shop) => shop.id === currentShopId)?.name || fallbackShopName

  // Show back button on detail pages (paths with IDs)
  const isDetailPage = /\/\w+\/[^/]+$/.test(location.pathname) && location.pathname !== '/'

  return (
    <header
      className={cn(
        // Same responsive px as .page-shell so the header lines up with
        // page content at every breakpoint.
        'sticky top-0 z-sticky flex h-nav items-center gap-3 border-b border-border bg-surface-primary/80 backdrop-blur-xl px-4 sm:px-6 lg:px-8',
        className
      )}
    >
      {/* Left: back or shop name */}
      <div className="flex items-center gap-2 min-w-0 flex-1">
        {isDetailPage ? (
          <IconButton onClick={() => navigate(-1)} aria-label="Go back">
            <ArrowLeft />
          </IconButton>
        ) : (
          <span className="text-sm font-bold tracking-[0.14em] text-primary-500 uppercase truncate md:hidden">
            {shopName}
          </span>
        )}
      </div>

      {/* Center: page title */}
      <h1 className="text-sm font-semibold text-content-primary truncate md:text-base">
        {pageTitle}
      </h1>

      {/* Right: actions */}
      <div className="flex items-center gap-2 min-w-0 flex-1 justify-end">
        {actions}
        <ThemeSwitcher />
      </div>
    </header>
  )
}
